import React, { useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import StaffSectionLayout from '../../../components/staff/StaffSectionLayout';
import ContentBox from '../../../components/ContentBox';
import { FpButton, Field, TextInput, Select, RarityPill, EmptyState } from '../../../components/frankopop/frankopopShared';
import {
    useFrankopopData, actorFromUser, fetchEligibleSets, fetchSet,
    fetchWeeklyUsage, fetchRecentGifts, giftBox, STAFF_WEEKLY_LIMIT, relativeTime,
} from '../../../data/frankopop';

function RulesPanel() {
    return (
        <ContentBox theme="default" tint="lightskyblue" header="How gifting works">
            <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li><strong>Normal staff</strong> - can only gift from sets marked <em>Anyone on staff</em>, and at most <strong>{STAFF_WEEKLY_LIMIT} boxes per set per week</strong>. Sets marked <em>Admins only</em> don&apos;t appear.</li>
                <li><strong>Admins</strong> - can gift from <strong>any</strong> set (shop sets included), with <strong>no weekly limit</strong>, and may pick the <strong>exact FrankoPop</strong> inside the box.</li>
                <li>A gifted box arrives sealed. The recipient opens it from <em>My Collection</em>; the FrankoPop is rolled then (unless an admin fixed the contents).</li>
                <li>LTD FrankoPops can never be pushed past their cap - not by the roll, and not by an admin choosing them.</li>
            </ul>
        </ContentBox>
    );
}

export default function FrankoPopAward({ userData }) {
    const actor = actorFromUser(userData);
    const isStaff = !!userData?.hasStaffRole;

    const [recipient, setRecipient] = useState(actor.name);
    const [setId, setSetId] = useState('');
    const [forcedFigureId, setForcedFigureId] = useState('');

    const { data: eligibleSets, refetch: refetchEligible } = useFrankopopData(
        useCallback(() => (isStaff ? fetchEligibleSets() : Promise.resolve([])), [isStaff]), []
    );
    const sets = eligibleSets || [];
    const effectiveSetId = setId || sets[0]?.id || '';
    const selectedSet = sets.find((s) => s.id === effectiveSetId) || null;

    const { data: figuresData } = useFrankopopData(
        useCallback(
            () => (actor.isAdmin && effectiveSetId ? fetchSet(effectiveSetId).then((r) => r.figures) : Promise.resolve([])),
            [actor.isAdmin, effectiveSetId]
        ), []
    );
    const figures = figuresData || [];

    const { data: usageData, refetch: refetchUsage } = useFrankopopData(
        useCallback(
            () => (!actor.isAdmin && effectiveSetId ? fetchWeeklyUsage(effectiveSetId) : Promise.resolve(null)),
            [actor.isAdmin, effectiveSetId]
        ), null
    );
    const used = usageData ? usageData.used : 0;
    const atLimit = !actor.isAdmin && used >= STAFF_WEEKLY_LIMIT;

    const { data: gifts, refetch: refetchGifts } = useFrankopopData(
        useCallback(() => (isStaff ? fetchRecentGifts() : Promise.resolve([])), [isStaff]), []
    );

    if (!userData || !userData.hasStaffRole) return <Navigate to="/" replace />;

    const submit = async () => {
        const res = await giftBox({
            setId: effectiveSetId,
            recipientHabboName: recipient,
            forcedFigureId: actor.isAdmin && forcedFigureId ? forcedFigureId : undefined,
        });
        if (!res.success) { toast.error(res.message); return; }
        toast.success(`Box from ${selectedSet.name} gifted to ${recipient.trim() || actor.name}.`);
        setForcedFigureId('');
        refetchUsage();
        refetchGifts();
        refetchEligible();
    };

    return (
        <StaffSectionLayout>
            <ContentBox theme="default" tint="lightskyblue" header="Award a FrankoPop box">
                {sets.length === 0 ? (
                    <EmptyState
                        title="No sets you can gift from"
                        sub={actor.isAdmin ? 'Create a set first.' : 'No sets are currently marked "Anyone on staff".'}
                    />
                ) : (
                    <div className="space-y-4 max-w-lg">
                        <Field label="Recipient" hint="Defaults to you so the box lands in your own collection for testing.">
                            <TextInput value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Habbo name" />
                        </Field>

                        <Field label="Set">
                            <Select value={effectiveSetId} onChange={(e) => { setSetId(e.target.value); setForcedFigureId(''); }}>
                                {sets.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}{s.giftableOnly ? '' : ' (shop set)'} - {s.figureCount} FrankoPops
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        {actor.isAdmin && selectedSet && (
                            <Field label="Contents" hint="Admins only. Leave as random for a normal blind box.">
                                <Select value={forcedFigureId} onChange={(e) => setForcedFigureId(e.target.value)}>
                                    <option value="">Random (rolled on open)</option>
                                    {figures.map((f) => (
                                        <option key={f.id} value={f.id} disabled={f.ltdExhausted}>
                                            #{String(f.number).padStart(2, '0')} {f.name} - {f.rarityName}
                                            {f.ltd ? ` (LTD ${f.ltdRemaining}/${f.ltdTotal} left)` : ''}
                                            {f.ltdExhausted ? ' - SOLD OUT' : ''}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                        )}

                        <div className="border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                            {actor.isAdmin ? (
                                <span className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-green-700">Unlimited (admin)</span>
                            ) : (
                                <span className={`font-['Jersey_10'] uppercase text-[13px] tracking-[1px] ${atLimit ? 'text-red-600' : 'text-gray-700'}`}>
                                    This week from {selectedSet?.name}: {used} / {STAFF_WEEKLY_LIMIT} gifted
                                </span>
                            )}
                        </div>

                        <FpButton variant="primary" onClick={submit} disabled={!effectiveSetId || atLimit || !recipient.trim()}>
                            {atLimit ? 'Weekly limit reached' : 'Gift box'}
                        </FpButton>
                    </div>
                )}
            </ContentBox>

            <ContentBox theme="default" tint="lightskyblue" header="Recent gifts">
                {!gifts || gifts.length === 0 ? (
                    <p className="text-sm text-gray-500">No boxes gifted yet.</p>
                ) : (
                    <div className="space-y-2">
                        {gifts.map((g) => (
                            <div key={g.id} className="flex items-center gap-3 border border-gray-200 bg-white px-3 py-2 text-sm" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.08)' }}>
                                <span className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-800">{g.by}</span>
                                <span className="text-gray-400">→</span>
                                <span className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-800">{g.to}</span>
                                <span className="text-gray-500">{g.set?.name}</span>
                                {g.figure && <RarityPill name={g.figure.rarityName} colour={g.figure.rarityColour} />}
                                {g.viaAdmin && <span className="font-['Jersey_10'] uppercase text-[10px] tracking-[1px] text-purple-600">admin</span>}
                                <span className="ml-auto text-[11px] text-gray-400">{relativeTime(g.at)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </ContentBox>

            <RulesPanel />
        </StaffSectionLayout>
    );
}
